/**
 * Pure utility functions for image resize clamping logic.
 * Extracted for independent testability.
 *
 * Requirements: 3.4
 */

export interface Dimensions {
  width: number;
  height: number;
}

export interface ClampOptions {
  /** Minimum allowed dimension in pixels (default: 50) */
  minSize: number;
  /** Maximum allowed width in pixels (editor content area width) */
  maxWidth: number;
}

const DEFAULT_MIN_SIZE = 50;

/**
 * Clamp dimensions between minimum and maximum constraints while maintaining
 * the aspect ratio.
 *
 * Rules:
 * - Neither width nor height can go below minSize (50px)
 * - Width cannot exceed maxWidth (editor area width)
 * - Aspect ratio is preserved
 * - If constraints conflict (e.g., maxWidth would make height < minSize),
 *   the minimum takes precedence
 */
export function clampDimensions(
  desired: Dimensions,
  options: ClampOptions
): Dimensions {
  const { minSize, maxWidth } = options;
  const effectiveMin = Math.max(minSize, DEFAULT_MIN_SIZE);
  const effectiveMaxWidth = Math.max(maxWidth, effectiveMin);

  let { width, height } = desired;

  // Ensure positive values
  width = Math.max(width, 1);
  height = Math.max(height, 1);

  const aspectRatio = width / height;

  // Clamp width to maxWidth
  if (width > effectiveMaxWidth) {
    width = effectiveMaxWidth;
    height = width / aspectRatio;
  }

  // Clamp width to minimum
  if (width < effectiveMin) {
    width = effectiveMin;
    height = width / aspectRatio;
  }

  // Clamp height to minimum
  if (height < effectiveMin) {
    height = effectiveMin;
    width = height * aspectRatio;
  }

  // Final safety: ensure width doesn't exceed max after height clamp
  if (width > effectiveMaxWidth) {
    width = effectiveMaxWidth;
  }

  // Ensure minimums are always respected (final guard)
  width = Math.max(width, effectiveMin);
  height = Math.max(height, effectiveMin);

  return {
    width: Math.round(width),
    height: Math.round(height),
  };
}

/**
 * Calculate new dimensions during a resize drag operation.
 * Takes the original dimensions, the mouse delta, and produces new dimensions
 * that maintain aspect ratio.
 */
export function calculateResizeDimensions(
  original: Dimensions,
  deltaX: number,
  deltaY: number,
  options: ClampOptions
): Dimensions {
  const aspectRatio = original.width / original.height;

  // Use the larger delta to drive the resize (maintaining aspect ratio)
  // For bottom-right handle, both deltaX and deltaY are positive when growing
  const deltaByX = deltaX;
  const deltaByY = deltaY * aspectRatio;

  // Use whichever delta produces the larger absolute change
  const effectiveDelta = Math.abs(deltaByX) >= Math.abs(deltaByY)
    ? deltaByX
    : deltaByY;

  const newWidth = original.width + effectiveDelta;
  const newHeight = newWidth / aspectRatio;

  return clampDimensions({ width: newWidth, height: newHeight }, options);
}
